import { useState } from "react";
import { supabase } from "../lib/supabase";
import { PRONOUN_CHOICES, type Pronouns } from "../shared/pronouns";

// One tap, skippable, and quiet about it.
//
// The reason it exists is worth being honest about on screen, because "what are
// your pronouns" with no context reads as a form asking somebody to categorise
// themselves. Saying it is so the questions about you read right makes it a
// practical question about the game rather than a demographic one.
//
// Skipping is a real answer with a real outcome: the generator is told to use
// the person's name and never guess, which is better than a wrong guess and
// better than a blank it fills in for itself.
export function PronounPicker({
  userId, current, onDone, compact = false,
}: {
  userId: string;
  current: string | null;
  onDone?: (value: string | null) => void;
  compact?: boolean;
}) {
  const [value, setValue] = useState<string | null>(current);
  const [saving, setSaving] = useState(false);
  // "Has not answered" and "chose not to say" are different states that both
  // store null. Collapsing them made "Rather not say" look pre-selected before
  // anybody touched it, which is presumptuous on a question specifically about
  // how somebody wants to be referred to.
  const [chosen, setChosen] = useState(current !== null);

  async function choose(p: Pronouns | null) {
    setSaving(true);
    setValue(p);
    setChosen(true);
    // pronouns_asked is set either way. Declining is an answer, and the one
    // thing a question like this must not do is keep asking.
    await supabase.from("users").update({ pronouns: p, pronouns_asked: true }).eq("id", userId);
    setSaving(false);
    onDone?.(p);
  }

  return (
    <div className="card">
      <span className="tag">How should we talk about you?</span>
      {!compact && (
        <p className="muted">
          So the questions about you read right. One tap, and you can skip it.
        </p>
      )}
      <div className="chips">
        {PRONOUN_CHOICES.map((c) => (
          <button
            key={c.value}
            className={`chip${value === c.value ? " current" : ""}`}
            disabled={saving}
            onClick={() => void choose(c.value)}
          >
            {c.label}
          </button>
        ))}
        <button
          className={`chip${chosen && value === null ? " current" : ""}`}
          disabled={saving}
          onClick={() => void choose(null)}
        >
          Rather not say
        </button>
      </div>
      {chosen && value === null && (
        <p className="muted" style={{ fontSize: 13 }}>
          We&apos;ll just use your name.
        </p>
      )}
    </div>
  );
}
